import { BulkActionDescriptor, MetadataEntityFieldType } from '@jms/model';

/** Section of the bulk actions palette. A null label renders the entries without a header. */
export interface BulkCommandGroup<TEntry> {
  label: string | null;
  entries: TEntry[];
}

/**
 * What a descriptor asks of the operator: an action fires immediately or opens its own dialog, whereas a
 * field update takes a value in the panel first. Custom fields split out from first-class ones because a
 * tenant can configure many, and mixed together they bury the built-in fields.
 */
export type BulkCommandKind = 'action' | 'field' | 'customField';

const KIND_ORDER: readonly BulkCommandKind[] = ['action', 'field', 'customField'];

/**
 * Custom fields are labelled "Additional Fields" for operators. The internal `customField` naming stays,
 * because it's what ties these descriptors to their `customFields.` metadata paths.
 */
const KIND_LABEL: Record<BulkCommandKind, string> = {
  action: 'Actions',
  field: 'Set Fields',
  customField: 'Set Additional Fields',
};

export function resolveBulkCommandKind(descriptor: BulkActionDescriptor | null | undefined): BulkCommandKind {

  // A descriptor the palette can't classify still has to be reachable; actions is the inert bucket.
  if (!descriptor || descriptor.kind === 'command') return 'action';
  return isCustomFieldDescriptor(descriptor) ? 'customField' : 'field';
}

/**
 * Custom-field paths always carry a `customFields.` segment — `customFields.dt_x` at the root,
 * `workOrders.customFields.dt_x` when merged from an embedded level, `entity.workOrders.customFields.dt_x`
 * on a queue board. The editor's field type is the fallback for a descriptor with no field path.
 */
export function isCustomFieldDescriptor(descriptor: BulkActionDescriptor): boolean {

  if (/(^|\.)customFields\./.test(descriptor.field ?? '')) return true;

  const editorField = descriptor.editor?.fields?.[0];
  const fieldType = Array.isArray(editorField?.type) ? editorField.type[0] : editorField?.type;
  return fieldType === MetadataEntityFieldType.CustomField;
}

/**
 * Buckets palette entries by record grain and then by kind.
 *
 * Grain labels are only applied when the palette holds more than one — a scoped board or an Entity queue
 * resolves to a single grain, where "Stages · update fields" is noise. A flag queue holds Flag, Job and
 * Stage together with overlapping field names, where the grain is the only thing telling them apart.
 *
 * Grains appear in the order first encountered, so callers control grain order by the order they pass
 * entries in. Within a grain the order is fixed: actions, fields, custom fields.
 *
 * @param grainOf Grain label for a descriptor, or null for one that belongs to no particular grain.
 */
export function groupBulkCommandsByGrain<TEntry>(
  entries: readonly TEntry[],
  options: {
    descriptorOf: (entry: TEntry) => BulkActionDescriptor | null | undefined;
    grainOf: (descriptor: BulkActionDescriptor) => string | null;
  },
): BulkCommandGroup<TEntry>[] {

  const classified = entries.map(entry => {
    const descriptor = options.descriptorOf(entry);
    return { entry, grain: descriptor ? options.grainOf(descriptor) : null, kind: resolveBulkCommandKind(descriptor) };
  });

  const grains = [...new Set(classified.map(item => item.grain).filter((grain): grain is string => grain != null))];
  const section = (label: string, matches: typeof classified) => matches.length ? [{ label, entries: matches.map(item => item.entry) }] : [];

  // One grain means the label adds nothing, so every section collapses to its kind — including entries
  // that resolved to no grain, which would otherwise produce a second section under the same label.
  if (grains.length <= 1) {
    return KIND_ORDER.flatMap(kind => section(KIND_LABEL[kind], classified.filter(item => item.kind === kind)));
  }

  return [
    ...grains.flatMap(grain => KIND_ORDER.flatMap(kind =>
      section(`${grain} · ${KIND_LABEL[kind].toLowerCase()}`, classified.filter(item => item.grain === grain && item.kind === kind)))),
    ...KIND_ORDER.flatMap(kind => section(KIND_LABEL[kind], classified.filter(item => item.grain == null && item.kind === kind))),
  ];
}
